/**
 * UAMP (Universal Agentic Message Protocol) Types for Robutler
 * Based on UAMP v1.0 specification
 * Compatible with WebAgents SDK
 */

export const UAMP_VERSION = '1.0';

// Modalities
export type Modality = 'text' | 'audio' | 'image' | 'video' | 'file' | 'html' | string;

export type AudioFormat = 'pcm16' | 'g711_ulaw' | 'g711_alaw' | 'mp3' | 'opus' | 'wav' | 'webm' | 'aac';
export type ImageFormat = 'jpeg' | 'png' | 'webp' | 'gif';
export type VideoFormat = 'mp4' | 'webm';

// Message roles
export type MessageRole = 'user' | 'assistant' | 'system' | 'tool';

// Content types for multimodal messages
export interface TextContent {
  type: 'text';
  text: string;
}

export interface AudioContent {
  type: 'audio';
  audio: string | { url: string };
  format?: AudioFormat;
  duration_ms?: number;
  content_id?: string;
  description?: string;
  display_hint?: DisplayHint;
}

export interface ImageContent {
  type: 'image';
  image: string | { url: string };
  format?: ImageFormat;
  detail?: 'low' | 'high' | 'auto';
  alt_text?: string;
  content_id?: string;
  description?: string;
  display_hint?: DisplayHint;
}

export interface VideoContent {
  type: 'video';
  video: string | { url: string };
  format?: VideoFormat;
  duration_ms?: number;
  thumbnail?: string;
  content_id?: string;
  description?: string;
  display_hint?: DisplayHint;
}

export interface FileContent {
  type: 'file';
  file: string | { url: string };
  filename: string;
  mime_type: string;
  size_bytes?: number;
  content_id?: string;
  description?: string;
  display_hint?: DisplayHint;
}

export interface ToolCallContent {
  type: 'tool_call';
  tool_call: {
    id: string;
    name: string;
    arguments: string; // JSON string
  };
}

export interface ToolResultContent {
  type: 'tool_result';
  tool_result: {
    call_id: string;
    result: string; // JSON string
    is_error?: boolean;
    content_items?: ContentItem[];
  };
}

export interface HtmlContent {
  type: 'html';
  html: string | { url: string };
  title?: string;
  sandbox?: boolean;
  dimensions?: { width: number; height: number };
  content_id?: string;
  description?: string;
  display_hint?: DisplayHint;
  /**
   * v3 Plan 1 — `live` primitive metadata. Present when the producer
   * publishes a real-time stream addressed by `live.id` (a duplex
   * 1→N broadcast: agent produces, widgets attach). The chat-view
   * dispatcher, the bridge live ops, and the SDK's `host.live` API
   * all key off this object.
   */
  live?: LiveMeta;
  /**
   * v3 Plan 1 — capture metadata. Set when the html block embodies
   * a captured-from-live snapshot (e.g. a still frame of a browser
   * stream pinned to the chat thread after `live.end`).
   */
  capture?: LiveCapture;
}

export interface ThinkingContent {
  type: 'thinking';
  text: string;
}

/**
 * How the client should render a content item.
 *
 * - 'inline'      — embed in chat thread (default for text/html-without-live)
 * - 'attachment'  — collapsed chip with click-to-expand
 * - 'sandbox'     — full sandbox iframe (current widget render)
 * - 'pinned'      — v3 Plan 1: pinned live block; sticky at top of chat thread
 * - 'compact-chip'— v3 Plan 1: dim/minimised live chip; non-sticky
 * - 'canvas'      — v3 Plan 1: render only on canvas (suppresses chat block)
 */
export type DisplayHint =
  | 'inline'
  | 'attachment'
  | 'sandbox'
  | 'pinned'
  | 'compact-chip'
  | 'canvas';

/**
 * v3 Plan 1 — transport hints attached to a `live` block. Producers
 * can offer multiple transports; consumers pick the best one they can
 * serve. The chat-view dispatcher prefers `iframe-url` (Case A — direct
 * widget URL) over `portal-relay` (Case B — bridge attaches and pipes
 * frames) over `webrtc` (peer-to-peer with portal as signaling).
 */
export type LiveTransport =
  | { kind: 'iframe-url'; url: string; allow?: string }
  | {
      kind: 'portal-relay';
      /**
       * Voice (Plan v3-03): the WS endpoint the widget opens to reach the
       * portal-held provider session, plus a short-lived bearer scoped to
       * (sessionId, userId). Only set on dispatcher-resolved relays; the
       * generic publish/consume path leaves these undefined.
       */
      relayUrl?: string;
      relayToken?: string;
    }
  | { kind: 'webrtc'; signalingChannelId: string };

/**
 * v3 Plan 1 — `live` primitive payload.
 *
 * `id` is the canonical liveId in `<kind>:<value>` form (e.g.
 * `content:abc-123`, `browser:sess-xyz`). The chat-view dispatcher
 * routes by the prefix of `id` via `liveDispatcher.register(prefix,
 * handler)` (see `lib/widgets/widget-ops/live-dispatcher.ts`).
 *
 * `transports` lists every wire the producer can serve; the consumer
 * picks the highest-capability transport it supports. Empty array
 * means "no live wire — render `html` only" (back-compat with v2).
 *
 * `widgetId` MAY identify a host-known widget the renderer should
 * use to surface this stream. If absent, the dispatcher falls back
 * to a generic viewer (the `browser-stream-viewer` widget for
 * `browser:*`, etc.).
 */
export interface LiveMeta {
  id: string;
  transports: LiveTransport[];
  /** Optional widget id to render the stream (e.g. 'browser-stream-viewer'). */
  widgetId?: string;
  /** Hint to the chat-view dispatcher: 'pinned' | 'compact-chip' | 'canvas'. */
  display?: 'pinned' | 'compact-chip' | 'canvas';
  /** ISO-8601 timestamp when the stream becomes invalid. Drives client TTL. */
  expiresAt?: string;
  /** Free-form producer hints — viewer widgets read these out of band. */
  meta?: Record<string, unknown>;
}

export interface LiveCapture {
  /** liveId the capture was sourced from. */
  fromLiveId: string;
  /** Capture timestamp (ISO-8601). */
  capturedAt: string;
  /** Optional thumbnail URL (e.g. compressed snapshot). */
  thumbnailUrl?: string;
}

// Union of all content types
export type ContentItem = 
  | TextContent 
  | AudioContent 
  | ImageContent 
  | VideoContent 
  | FileContent
  | HtmlContent
  | ToolCallContent
  | ToolResultContent
  | ThinkingContent;

// Message format (compatible with both simple and multimodal)
export interface UAMPMessage {
  role: MessageRole;
  content?: string; // Simple text content
  content_items?: ContentItem[]; // Multimodal content
  name?: string; // For tool messages
}

// Content delta for streaming
export interface ContentDelta {
  type: 'text' | 'audio' | 'image' | 'video' | 'html' | 'file' | 'tool_call' | 'tool_result' | 'tool_progress' | 'content_updated' | 'thinking';
  text?: string;
  audio?: string;
  image?: string | { url: string };
  video?: string | { url: string };
  html?: string | { url: string };
  file?: string | { url: string };
  filename?: string;
  mime_type?: string;
  content_id?: string;
  description?: string;
  display_hint?: DisplayHint;
  dimensions?: { width: number; height: number };
  title?: string;
  sandbox?: boolean;
  alt_text?: string;
  metadata?: Record<string, unknown>;
  tool_call?: {
    id?: string;
    name?: string;
    arguments?: string;
  };
  tool_result?: {
    call_id?: string;
    result?: string;
    status?: string;
    content_items?: ContentItem[];
  };
  tool_progress?: {
    call_id?: string;
    text?: string;
    replace?: boolean;
    media_type?: 'image' | 'video' | 'audio' | 'file' | '3d' | 'html';
    status?: 'queued' | 'generating' | 'processing' | 'uploading' | 'downloading' | 'complete' | 'failed';
    progress_percent?: number;
    estimated_duration_ms?: number;
    dimensions?: { width: number; height: number };
    thumbnail_url?: string;
  };
  content_updated?: {
    content_id: string;
    command: string;
    diff?: {
      old_str?: string;
      new_str?: string;
      insert_line?: number;
    };
    timestamp: number;
  };
  thinking?: {
    content?: string;
    is_delta?: boolean;
  };
}

// Response output item
export interface OutputItem {
  type: 'text' | 'audio' | 'image' | 'video' | 'html' | 'file' | 'tool_call' | 'tool_result';
  text?: string;
  audio?: string;
  image?: string | { url: string };
  video?: string | { url: string };
  html?: string | { url: string };
  file?: string | { url: string };
  filename?: string;
  mime_type?: string;
  content_id?: string;
  description?: string;
  display_hint?: DisplayHint;
  dimensions?: { width: number; height: number };
  title?: string;
  sandbox?: boolean;
  alt_text?: string;
  tool_call?: {
    id: string;
    name: string;
    arguments: string;
  };
  tool_result?: {
    call_id: string;
    result: string;
    is_error?: boolean;
    content_items?: ContentItem[];
  };
}

// Response object
export interface UAMPResponse {
  id: string;
  status: 'completed' | 'cancelled' | 'failed' | 'incomplete';
  output: OutputItem[];
  usage?: UsageStats;
  /**
   * Why the provider stopped, in its own word (`STOP`, `MALFORMED_FUNCTION_CALL`,
   * `length`, ...). Sent by the LLM proxy when the provider reported one; the
   * SDKs read it when `output` is empty (2026-09-27).
   */
  finish_reason?: string;
  /** True when the PROMPT was blocked (a safety or recitation verdict), not the completion cut. */
  finish_blocked?: boolean;
}

// Usage statistics
export interface UsageStats {
  input_tokens?: number;
  output_tokens?: number;
  total_tokens?: number;
  cached_tokens?: number;
  /**
   * The CREDITS the platform charged for the call: its settle's total
   * deducted, a JSON number (B4, 2026-09-28). Sent by the LLM proxy on
   * `response.done` and by the completions route in `usage`; absent when
   * the platform did not meter the call. Both SDKs show it as the charge.
   */
  total_cost?: number;
  cost?: {
    input_cost?: number;
    output_cost?: number;
    total_cost?: number;
    currency?: string;
  };
  audio?: {
    input_seconds?: number;
    output_seconds?: number;
  };
}

// Voice configuration
export interface VoiceConfig {
  provider?: string;
  voice_id?: string;
  name?: string;
  speed?: number;
  pitch?: number;
  language?: string;
  extensions?: Record<string, unknown>;
}

// Turn detection for voice
export interface TurnDetectionConfig {
  type: 'server_vad' | 'client_vad' | 'push_to_talk' | 'none';
  threshold?: number;
  silence_duration_ms?: number;
  prefix_padding_ms?: number;
  extensions?: Record<string, unknown>;
}
/** @deprecated Use TurnDetectionConfig */
export type TurnDetection = TurnDetectionConfig;

// Function-based tool definition (OpenAI compatible)
export interface FunctionToolDefinition {
  type: 'function';
  function: {
    name: string;
    description?: string;
    parameters?: Record<string, unknown>;
  };
}

// Native/built-in tool definition for provider-specific tools
export interface NativeToolDefinition {
  type: string;
  [key: string]: unknown;
}

export type ToolDefinition = FunctionToolDefinition | NativeToolDefinition;

// Session configuration
export interface SessionConfig {
  modalities: Modality[];
  instructions?: string;
  tools?: ToolDefinition[];
  voice?: VoiceConfig;
  input_audio_format?: AudioFormat;
  output_audio_format?: AudioFormat;
  turn_detection?: TurnDetectionConfig;
  extensions?: Record<string, unknown>;
}

// Capabilities (unified format for model/client/agent)
export interface ImageCapabilities {
  formats: ImageFormat[];
  max_size_bytes?: number;
  max_pixels?: number;
  detail_levels?: ('low' | 'high' | 'auto')[];
}

export interface AudioCapabilities {
  input_formats: AudioFormat[];
  output_formats: AudioFormat[];
  sample_rates?: number[];
  max_duration_seconds?: number;
  supports_realtime: boolean;
  voices?: string[];
}

export interface FileCapabilities {
  supported_mime_types: string[];
  max_size_bytes?: number;
  supports_pdf: boolean;
  supports_code: boolean;
  supports_structured_data: boolean;
}

export interface ToolCapabilities {
  supports_tools: boolean;
  supports_parallel_tools: boolean;
  supports_streaming_tools: boolean;
  max_tools_per_request?: number;
  built_in_tools: string[];
}

export interface Capabilities {
  id: string;
  provider: string;
  modalities: Modality[];
  image?: ImageCapabilities;
  audio?: AudioCapabilities;
  file?: FileCapabilities;
  tools?: ToolCapabilities;
  supports_streaming: boolean;
  supports_thinking: boolean;
  supports_caching: boolean;
  context_window?: number;
  max_output_tokens?: number;
  provides?: string[];
  widgets?: string[];
  endpoints?: string[];
  output_content_types?: Modality[];
  supports_rich_display?: boolean;
  extensions?: Record<string, unknown>;
}

// Error object
export interface UAMPError {
  code: string;
  message: string;
  details?: Record<string, unknown>;
}

// Progress update
export interface Progress {
  target?: string;
  stage?: string;
  message?: string;
  percent?: number;
  step?: number;
  total_steps?: number;
}

// Thinking/reasoning content
export interface Thinking {
  content: string;
  stage?: string;
  redacted?: boolean;
  is_delta?: boolean;
}
